import { useMemo, useState, type ReactNode } from 'react';
import {
  API_SURFACE_LABELS,
  ENVIRONMENT_IDS,
  type ApiEndpoint,
  type ApiSurface,
  type EnvironmentId,
  type HealthReport,
  type ProbeResult,
} from '@sfpbxtestbot/contracts';
import { api } from '../lib/api.js';
import { useAsync, useRemembered } from '../lib/useAsync.js';
import {
  Button,
  Callout,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Stat,
  StatusBadge,
  formatRelative,
} from '../components/primitives.js';

const SURFACE_EXPLAINER: Record<ApiSurface, string> = {
  admin:
    'The provisioning API. It creates tenants, users, extensions and call routing, and reports live calls and call records back.',
  gateway:
    'The API the Salesforce softphone calls. If something here is red, agents feel it directly inside Salesforce.',
};

/**
 * PBX API Health.
 *
 * Two surfaces, grouped by what each group of endpoints is for, with a
 * plain-English consequence attached to every failure. The aim is that someone
 * outside engineering can look at this page and say what is broken for whom.
 */
export function ApiHealthPage(): ReactNode {
  const catalog = useAsync(() => api.catalog(), []);
  const history = useAsync(() => api.healthReports(10), []);

  const [environment, setEnvironment] = useRemembered<EnvironmentId>('health.environment', 'qa');
  const [tenantCode, setTenantCode] = useRemembered('health.tenantCode', '');
  const [report, setReport] = useState<HealthReport>();
  const [runningGroup, setRunningGroup] = useState<string>();
  const [error, setError] = useState<string>();

  const endpoints = catalog.data?.endpoints ?? [];
  const resultsById = useMemo(() => {
    const map = new Map<string, ProbeResult>();
    for (const result of report?.results ?? []) map.set(result.endpointId, result);
    return map;
  }, [report]);

  const slowest = useMemo(() => {
    const latencies = (report?.results ?? [])
      .map((result) => result.latencyMs)
      .filter((value): value is number => value != null);
    return latencies.length > 0 ? Math.max(...latencies) : 0;
  }, [report]);

  async function check(scope: { surfaces: ApiSurface[]; groups?: string[]; label: string }): Promise<void> {
    setRunningGroup(scope.label);
    setError(undefined);
    try {
      const next = await api.runHealthCheck({
        environment,
        surfaces: scope.surfaces,
        groups: scope.groups ?? [],
        tenantCode: tenantCode || undefined,
        readOnlyOnly: false,
      });
      // A group check should update its own cards without wiping the rest.
      setReport((current) => (current ? mergeReports(current, next) : next));
      history.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The health check could not be run.');
    } finally {
      setRunningGroup(undefined);
    }
  }

  return (
    <div className="stack">
      <PageHeader
        title="PBX API health"
        lede="RocketPBX has two separate APIs and they fail independently. This checks both, and tells you what each failure means for the people using the phone system."
        actions={
          <>
            <a className="button button--ghost button--small" href={api.catalogMarkdownUrl} target="_blank" rel="noreferrer">
              Endpoint list
            </a>
            <a className="button button--ghost button--small" href={api.catalogCsvUrl}>
              Export CSV
            </a>
            <Button
              variant="primary"
              busy={runningGroup === 'everything'}
              disabled={Boolean(runningGroup)}
              onClick={() => void check({ surfaces: ['admin', 'gateway'], label: 'everything' })}
            >
              Check everything
            </Button>
          </>
        }
      />

      <Card>
        <div className="grid grid--3">
          <Field label="Environment" hint="Decides which gateway and hosts are checked.">
            <select
              className="select"
              value={environment}
              onChange={(event) => setEnvironment(event.target.value as EnvironmentId)}
            >
              {ENVIRONMENT_IDS.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Tenant code" hint="Optional. Scopes tenant-specific endpoints.">
            <input
              className="input"
              value={tenantCode}
              placeholder="acme"
              onChange={(event) => setTenantCode(event.target.value)}
            />
          </Field>
          <div className="stack stack--tight" style={{ justifyContent: 'center' }}>
            <span className="field__label">Last checked</span>
            <span className="muted">{report ? formatRelative(report.startedAt) : 'Not yet'}</span>
          </div>
        </div>
      </Card>

      {error ? <Callout tone="danger">{error}</Callout> : null}

      {report ? <HealthSummary report={report} /> : null}

      {catalog.loading ? (
        <Card>
          <EmptyState title="Loading the endpoint catalogue" body="One moment." />
        </Card>
      ) : null}

      {(['gateway', 'admin'] as const).map((surface) => (
        <SurfaceSection
          key={surface}
          surface={surface}
          endpoints={endpoints.filter((endpoint) => endpoint.surface === surface)}
          groups={catalog.data?.groups[surface] ?? []}
          results={resultsById}
          slowest={slowest}
          runningGroup={runningGroup}
          onCheckGroup={(group) =>
            void check({ surfaces: [surface], groups: [group], label: `${surface}:${group}` })
          }
          onCheckSurface={() => void check({ surfaces: [surface], label: surface })}
        />
      ))}

      <Card title="Recent checks" subtitle="Every check is kept so you can compare against a known-good day.">
        {(history.data?.reports.length ?? 0) === 0 ? (
          <EmptyState title="No checks recorded yet" body="Run a check and it will be saved here." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Environment</th>
                <th>Result</th>
                <th>Passed</th>
                <th>Failed</th>
                <th>Skipped</th>
              </tr>
            </thead>
            <tbody>
              {history.data?.reports.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatRelative(entry.startedAt)}</td>
                  <td>{entry.environment}</td>
                  <td>
                    <StatusBadge outcome={entry.outcome} />
                  </td>
                  <td>{entry.totals.passed}</td>
                  <td>{entry.totals.failed}</td>
                  <td>{entry.totals.skipped}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function HealthSummary({ report }: { report: HealthReport }): ReactNode {
  const failures = report.results.filter((result) => result.outcome === 'failed');

  return (
    <Card>
      <div className="row row--between" style={{ marginBottom: failures.length > 0 ? 16 : 0 }}>
        <div className="row" style={{ gap: 40 }}>
          <Stat value={report.totals.passed} label="Healthy" tone="passed" />
          <Stat value={report.totals.failed} label="Failing" tone={report.totals.failed > 0 ? 'failed' : undefined} />
          <Stat value={report.totals.warning} label="Needs a look" tone={report.totals.warning > 0 ? 'warning' : undefined} />
          <Stat value={report.totals.skipped} label="Not checked" />
        </div>
        <StatusBadge outcome={report.outcome} />
      </div>

      {failures.length > 0 ? (
        <Callout tone="danger">
          <div>
            <strong>{failures.length === 1 ? 'One endpoint is failing.' : `${failures.length} endpoints are failing.`}</strong>{' '}
            Expand the affected group below to see which, and what it breaks.
          </div>
        </Callout>
      ) : null}
    </Card>
  );
}

function SurfaceSection({
  surface,
  endpoints,
  groups,
  results,
  slowest,
  runningGroup,
  onCheckGroup,
  onCheckSurface,
}: {
  surface: ApiSurface;
  endpoints: ApiEndpoint[];
  groups: string[];
  results: Map<string, ProbeResult>;
  slowest: number;
  runningGroup: string | undefined;
  onCheckGroup: (group: string) => void;
  onCheckSurface: () => void;
}): ReactNode {
  return (
    <Card
      title={API_SURFACE_LABELS[surface]}
      subtitle={SURFACE_EXPLAINER[surface]}
      actions={
        <Button
          size="small"
          busy={runningGroup === surface}
          disabled={Boolean(runningGroup)}
          onClick={onCheckSurface}
        >
          Check this API
        </Button>
      }
    >
      <div className="stack stack--tight">
        {groups.map((group) => (
          <GroupCard
            key={group}
            group={group}
            endpoints={endpoints.filter((endpoint) => endpoint.group === group)}
            results={results}
            slowest={slowest}
            busy={runningGroup === `${surface}:${group}`}
            disabled={Boolean(runningGroup)}
            onCheck={() => onCheckGroup(group)}
          />
        ))}
      </div>
    </Card>
  );
}

function GroupCard({
  group,
  endpoints,
  results,
  slowest,
  busy,
  disabled,
  onCheck,
}: {
  group: string;
  endpoints: ApiEndpoint[];
  results: Map<string, ProbeResult>;
  slowest: number;
  busy: boolean;
  disabled: boolean;
  onCheck: () => void;
}): ReactNode {
  const checked = endpoints.map((endpoint) => results.get(endpoint.id)).filter(Boolean) as ProbeResult[];
  const failing = checked.filter((result) => result.outcome === 'failed').length;
  const [expanded, setExpanded] = useState(false);

  // A group with a failure opens itself: the operator should not have to hunt.
  const open = expanded || failing > 0;

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
      <div className="row row--between" style={{ padding: '12px 16px' }}>
        <button
          type="button"
          className="button button--ghost button--small"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={open}
        >
          {open ? '▾' : '▸'} {group}
          <span className="muted" style={{ fontWeight: 400 }}>
            {endpoints.length} endpoint{endpoints.length === 1 ? '' : 's'}
          </span>
        </button>
        <div className="row">
          {checked.length > 0 ? (
            <span className="muted" style={{ fontSize: 13 }}>
              {failing > 0 ? `${failing} failing` : `${checked.length} checked`}
            </span>
          ) : null}
          <Button size="small" busy={busy} disabled={disabled} onClick={onCheck}>
            Check
          </Button>
        </div>
      </div>

      {open ? (
        <div>
          {endpoints.map((endpoint) => (
            <EndpointRow
              key={endpoint.id}
              endpoint={endpoint}
              result={results.get(endpoint.id)}
              slowest={slowest}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EndpointRow({
  endpoint,
  result,
  slowest,
}: {
  endpoint: ApiEndpoint;
  result: ProbeResult | undefined;
  slowest: number;
}): ReactNode {
  const [showDetail, setShowDetail] = useState(false);
  const problem = result && result.outcome !== 'passed';

  return (
    <div>
      <div className="endpoint">
        <span className="endpoint__method">{endpoint.method}</span>
        <div className="endpoint__body">
          <span className="endpoint__title">{endpoint.title}</span>
          <span className="endpoint__path">{endpoint.path}</span>
        </div>
        <div className="endpoint__right">
          {result?.latencyMs != null ? (
            <>
              <LatencyBar value={result.latencyMs} max={slowest} />
              <span className="endpoint__latency">{result.latencyMs} ms</span>
            </>
          ) : null}
          {result ? <StatusBadge outcome={result.outcome} /> : <span className="muted" style={{ fontSize: 13 }}>Not checked</span>}
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => setShowDetail((value) => !value)}
            aria-expanded={showDetail}
          >
            {showDetail ? 'Hide' : 'Why it matters'}
          </button>
        </div>
      </div>

      {showDetail || problem ? (
        <div style={{ padding: '0 16px 14px 78px' }} className="stack stack--tight">
          {problem && result?.reason ? <Callout tone={result.outcome === 'failed' ? 'danger' : 'warning'}>{result.reason}</Callout> : null}
          {(showDetail || result?.outcome === 'failed') ? (
            <>
              <p style={{ fontSize: 13 }}>
                <strong>What it does.</strong> {endpoint.purpose}
              </p>
              <p style={{ fontSize: 13 }} className="muted">
                <strong>If it fails.</strong> {endpoint.impact}
              </p>
            </>
          ) : null}
          {showDetail && result?.responseSnippet ? (
            <details className="disclosure">
              <summary>Response</summary>
              <pre className="code-block">{result.responseSnippet}</pre>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function LatencyBar({ value, max }: { value: number; max: number }): ReactNode {
  const ceiling = Math.max(max, 1);
  const width = Math.max(4, Math.round((value / ceiling) * 100));
  const tone = value > 2_000 ? 'verySlow' : value > 800 ? 'slow' : undefined;

  return (
    <span className="latency-bar" title={`${value} ms`}>
      <span
        className={`latency-bar__fill${tone ? ` latency-bar__fill--${tone}` : ''}`}
        style={{ width: `${width}%` }}
      />
    </span>
  );
}

/**
 * Folds a group check into the report already on screen so that checking one
 * group does not blank out results for the others.
 */
function mergeReports(current: HealthReport, incoming: HealthReport): HealthReport {
  const byId = new Map(current.results.map((result) => [result.endpointId, result]));
  for (const result of incoming.results) byId.set(result.endpointId, result);
  const results = [...byId.values()];

  return {
    ...incoming,
    results,
    totals: results.reduce(
      (totals, result) => ({
        total: totals.total + 1,
        passed: totals.passed + (result.outcome === 'passed' ? 1 : 0),
        failed: totals.failed + (result.outcome === 'failed' ? 1 : 0),
        warning: totals.warning + (result.outcome === 'warning' ? 1 : 0),
        skipped: totals.skipped + (result.outcome === 'skipped' ? 1 : 0),
      }),
      { total: 0, passed: 0, failed: 0, warning: 0, skipped: 0 },
    ),
  };
}
